import { fetchAuthSession } from 'aws-amplify/auth';
import { getEnvironment, getApiBaseUrl } from '@/config/domains';

const environment = getEnvironment();
const API_BASE_URL = getApiBaseUrl();

console.log('Environment detected:', environment);
console.log('API Base URL:', API_BASE_URL);

// Validation error interface
export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface ApiError {
  status: number;
  error: string;
  details?: ValidationError[];
  message?: string;
}

// Geçerli JWT token'ı kontrol et ve durumunu konsola yazdır
async function checkAndLogAuthStatus() {
  try {
    const session = await fetchAuthSession();
    const isSignedIn = session?.tokens !== undefined;
    console.info('Auth status check - User is signed in:', isSignedIn);
    
    if (isSignedIn && session.tokens?.idToken) {
      // Token bilgilerini logla
      const accessToken = session.tokens.accessToken;
      const idToken = session.tokens.idToken;
      
      // Geçerlilik sürelerini hesapla
      const now = Math.floor(Date.now() / 1000);
      const accessExpiry = accessToken?.payload?.exp ? accessToken.payload.exp - now : -1;
      const idExpiry = idToken?.payload?.exp ? idToken.payload.exp - now : -1;
      
      console.info(`Access token expires in: ${accessExpiry} seconds`);
      console.info(`ID token expires in: ${idExpiry} seconds`);
      console.info('Using ID Token for API calls (correct for Cognito Authorizer)');
      console.info('ID Token payload claims:', JSON.stringify(idToken?.payload || {}, null, 2));
      
      // Önemli tenant_id bilgisi var mı?
      if (idToken.payload['custom:tenant_id']) {
        console.info('ID Token has tenant_id:', idToken.payload['custom:tenant_id']);
      } else {
        console.warn('ID Token is missing tenant_id claim! This may cause API authorization issues.');
      }
    }
  } catch (error) {
    console.error('Auth status check failed:', error);
  }
}

// Uygulama başladığında auth durumunu kontrol et
checkAndLogAuthStatus();

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getFullUrl(endpoint: string): string {
    // For localhost, add /api prefix to match dev-server routes
    const env = getEnvironment();
    if (env === 'localhost') {
      return `${this.baseURL}/api${endpoint}`;
    }
    return `${this.baseURL}${endpoint}`;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      // Debug için token bilgisinin varlığını kontrol et
      if (!token) {
        console.warn('Auth token is missing or undefined!');
      } else {
        console.info('Auth token obtained successfully');
      }
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  }

  private async handleResponse<T>(response: Response, fullUrl: string, requestData?: any): Promise<T> {
    console.info(`🔍 DEBUG: Response status: ${response.status}`);
    console.info('🔍 DEBUG: Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`🔍 DEBUG: API Error [${response.status}]:`, errorText);
      console.error('🔍 DEBUG: Request URL:', fullUrl);
      console.error('🔍 DEBUG: Request data:', requestData);
      console.error('🔍 DEBUG: Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Try to parse error response as JSON
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      // Create structured error
      const apiError: ApiError = {
        status: response.status,
        error: errorData.error || `HTTP ${response.status}`,
        message: errorData.message,
        details: errorData.details || []
      };
      
      // Throw error with structured data
      const error = new Error(apiError.error) as Error & { apiError: ApiError };
      error.apiError = apiError;
      throw error;
    }
    
    const responseData = await response.json();
    console.info('🔍 DEBUG: Response data:', responseData);
    return responseData;
  }

  async get<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    // Debug için istek detaylarını log'a yazdır
    console.info(`🔍 DEBUG: Making GET request to: ${fullUrl}`);
    console.info('🔍 DEBUG: With headers:', JSON.stringify(headers, null, 2));
    
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        credentials: 'include' // Bu, CORS isteklerinde çerezlerin ve auth bilgilerinin gönderilmesini sağlar
      });
      
      return this.handleResponse<T>(response, fullUrl);
    } catch (error) {
      console.error('🔍 DEBUG: Network error during API request:', error);
      throw error;
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    console.info(`Making POST request to: ${fullUrl}`);
    console.info('With headers:', JSON.stringify(headers, null, 2));
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include'
      });
      
      return this.handleResponse<T>(response, fullUrl, data);
    } catch (error) {
      console.error('Network error during API request:', error);
      throw error;
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    console.info(`Making PUT request to: ${fullUrl}`);
    console.info('With headers:', JSON.stringify(headers, null, 2));
    
    try {
      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include'
      });
      
      return this.handleResponse<T>(response, fullUrl, data);
    } catch (error) {
      console.error('Network error during API request:', error);
      throw error;
    }
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    console.info(`Making DELETE request to: ${fullUrl}`);
    console.info('With headers:', JSON.stringify(headers, null, 2));
    
    try {
      const response = await fetch(fullUrl, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      
      return this.handleResponse<T>(response, fullUrl);
    } catch (error) {
      console.error('Network error during API request:', error);
      throw error;
    }
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
